import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import { getAllProperties, getSubscriptionUsage } from "@/lib/api/api";
import { useAccountTestCounts } from "@/hooks/useAccountTestCounts";
import { formatCancelLossCopy } from "@/lib/constants/cancellationReasons";
import type { PlanUsageResponse } from "@/lib/types/billing";

type RawProperty = {
  propertyId?: string;
  isDemo?: boolean;
  archived?: boolean;
  isStaging?: boolean;
  is_staging?: boolean;
  stats?: { views?: number };
  subscription?: { currentViews?: number };
};

function isRealProperty(property: RawProperty): boolean {
  if (!property.propertyId) return false;
  if (property.isDemo || property.archived) return false;
  if (property.isStaging || property.is_staging) return false;
  return true;
}

function viewsFromProperty(property: RawProperty): number {
  const fromStats = Number(property.stats?.views);
  if (Number.isFinite(fromStats) && fromStats > 0) return fromStats;
  const fromSub = Number(property.subscription?.currentViews);
  return Number.isFinite(fromSub) && fromSub > 0 ? fromSub : 0;
}

function counterCurrent(data: PlanUsageResponse | null | undefined, key: "heatmaps" | "funnels"): number {
  const current = data?.[key]?.current;
  return typeof current === "number" && current > 0 ? current : 0;
}

/**
 * Account-level usage for the cancel-flow loss sentence. Queries stay idle
 * until the dialog is open so hub/settings mounts don't fan out extra work.
 */
export function useCancelUsageLoss(enabled: boolean): {
  copy: string | null;
  isLoading: boolean;
} {
  const { token } = useAuth();
  const { eventsUsed } = useSubscription();

  const propertiesQuery = useQuery({
    queryKey: ["cancel-usage-properties"],
    queryFn: async (): Promise<RawProperty[]> => {
      const data = await getAllProperties(token!);
      return Array.isArray(data?.properties) ? data.properties : [];
    },
    enabled: enabled && !!token,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const properties = useMemo(
    () =>
      (propertiesQuery.data ?? [])
        .filter(isRealProperty)
        .map((property) => ({
          ...property,
          propertyId: property.propertyId as string,
        })),
    [propertiesQuery.data],
  );

  const propertyIds = useMemo(
    () => properties.map((property) => property.propertyId),
    [properties],
  );

  const usageQueries = useQueries({
    queries: propertyIds.map((propertyId) => ({
      queryKey: ["plan-usage", propertyId],
      queryFn: () => getSubscriptionUsage(propertyId, token!),
      enabled: enabled && !!token && !!propertyId,
      staleTime: 30 * 1000,
      retry: false,
    })),
  });

  const tests = useAccountTestCounts({
    properties: enabled ? properties : [],
  });

  const heatmaps = usageQueries.reduce(
    (sum, query) => sum + counterCurrent(query.data, "heatmaps"),
    0,
  );
  const funnels = usageQueries.reduce(
    (sum, query) => sum + counterCurrent(query.data, "funnels"),
    0,
  );
  const usageLoading = usageQueries.some((query) => query.isLoading);
  const testsRan = Object.values(tests.byPropertyId).reduce(
    (sum, counts) => sum + (counts.total || 0),
    0,
  );
  const viewsFromSites = properties.reduce(
    (sum, property) => sum + viewsFromProperty(property),
    0,
  );
  const views = viewsFromSites > 0 ? viewsFromSites : eventsUsed;

  const copy = useMemo(
    () => formatCancelLossCopy({ heatmaps, funnels, tests: testsRan, views }),
    [funnels, heatmaps, testsRan, views],
  );

  const isLoading =
    enabled && (propertiesQuery.isLoading || usageLoading || tests.isLoading);

  return {
    copy: isLoading ? null : copy,
    isLoading,
  };
}
